import type { ContextSchema } from '@/vantage/types/Context.types';
import type {
  BaseColumnConfiguration,
  ExtensionColumnConfiguration,
  TableColumns,
} from '@/vantage/types/TableConfig.types';
import type {
  ColDef,
  GridApi,
  ICellRendererParams,
  ISetFilterParams,
  SetFilterParams,
  SetFilterValuesFuncParams,
} from 'ag-grid-community';
import {
  applyReduction,
  booleanOrValue,
  contextContent,
  nameToReferenceKey,
} from '@/vantage/components/evidence_table/helpers/utilities';
import _ from 'lodash';
import {
  generateComparator,
  generateDateComparator,
  generateDateFilterComparator,
} from '@/vantage/components/evidence_table/helpers/comparators';
import {
  ArrayColumnRenderer,
  BooleanColumnRenderer,
  SanitizedHtmlColumnRenderer,
} from '@/vantage/components/evidence_table/helpers/CustomColumnRenderers';
import { DurationFilter } from '@/vantage/components/evidence_table/helpers/DurationColumnFilter';
import {
  decimalFormatters,
  durationValueFormatter,
  generateDateValueFormatter,
  generateReferenceColumnValueFormatter,
  getNumericValueFormatter,
} from '@/vantage/components/evidence_table/helpers/valueFormatters';
import {
  booleanValueGetter,
  configValueGetter,
  possiblyZeroValueGetter,
  referenceValueGetter,
  transformationValueGetter,
  whiteSpaceRemoverValueGetter,
} from '@/vantage/components/evidence_table/helpers/valueGetters';
import DateColumnFilter from '@/vantage/components/evidence_table/helpers/DateColumnFilter';
import MultiModalColumnFilter from '@/vantage/components/evidence_table/helpers/MultiModalColumnFilter';
import { t } from 'i18next';
import { VANTAGE_UUID_COLUMNS, VantageNames } from '@/vantage/constants';
import { Icon } from '@seeqdev/qomponents';
import { GRANULARITY_OPTIONS } from '@/vantage/dateTime.utilities';
import { getColumnHeaderName, hasMultiModalFilter } from '@/vantage/components/evidence_table/helpers/column.utilities';
import { CapsuleEventType } from '@/vantage/components/evidence_table/helpers/column.constants';
import { translateLabelIfPossible } from '@/vantage/utilities';
import { isVantageServerSideRowModelEnabled } from '@/services/systemConfiguration.utilities';
import type { AnyProperty, WithRequired } from '@/utilities.types';
import type { ExtensionColumnDefinitionFilterType } from '@/vantage/types/Extension.types';
import { fetchDistinctValuesForColumn } from '@/serverSideTable/materializedTables/queries';
import { fetchAndSetValuesForSetColumnFilter } from '@/serverSideTable/serverSideTable.utilities';
import { SET_OPTIONS_FETCH_LIMIT } from '@/serverSideTable/serverSideTable.constants';
import { SeeqNames } from '@/main/app.constants.seeqnames';

type ColDefWithProperties<ExtraProperties extends AnyProperty> = ColDef & ExtraProperties;

export const MIN_COLUMN_WIDTH = 100;
const DEFAULT_COLUMN_WIDTH = 150;

/** @exportedfortesting */
export const itemsInGroupColumn: ColDef = {
  field: VantageNames.Columns.ItemsInGroup,
  headerName: 'VANTAGE.EVIDENCE_TABLE.COLUMNS.ITEMS_IN_GROUP',
  aggFunc: 'sum',
  chartDataType: 'series',
  enableRowGroup: false,
  filter: 'agNumberColumnFilter',
  valueFormatter: (params) => (!params.value ? '' : params.value),
  type: 'numericColumn',
  width: DEFAULT_COLUMN_WIDTH,
  minWidth: MIN_COLUMN_WIDTH,
};

/** @exportedfortesting */
export const capsuleCountColumn: ColDef = {
  field: 'descendant-items',
  headerName: 'VANTAGE.EVIDENCE_TABLE.COLUMNS.CAPSULE_COUNT',
  aggFunc: 'sum',
  chartDataType: 'series',
  enableRowGroup: false,
  filter: 'agNumberColumnFilter',
  valueFormatter: (params) => (!params.value ? '' : params.value),
  type: 'numericColumn',
  width: DEFAULT_COLUMN_WIDTH,
  minWidth: MIN_COLUMN_WIDTH,
};

const disableGrouping = (shouldDisable: boolean): ColDef =>
  shouldDisable
    ? {
        enableRowGroup: false,
        enableValue: false,
      }
    : {};

const disableSortingAndFilteringAndGrouping = (shouldDisable: boolean): ColDef =>
  shouldDisable
    ? {
        sortable: false,
        filter: false,
        ...disableGrouping(true),
      }
    : {};

const getFilterParams = (isServerSide: boolean, tableDefinitionId: string) => {
  const filters: AnyProperty[] = [
    {
      filter: 'agTextColumnFilter',
      filterParams: {
        defaultOption: 'contains',
      },
    },
    {
      filter: 'agSetColumnFilter',
      filterParams: {
        refreshValuesOnOpen: true,
        values: isServerSide
          ? async (params) =>
              fetchAndSetValuesForSetColumnFilter(params, () =>
                fetchDistinctValuesForColumn<string>(
                  tableDefinitionId,
                  params.column.getColId(),
                  SET_OPTIONS_FETCH_LIMIT,
                ),
              )
          : undefined,
      } as ISetFilterParams,
    },
  ];

  return { filters };
};

/** @exportedfortesting */
export const defaultBaseColumn = ({
  field: columnName,
  headerName,
  hide,
  conditionsList,
  isServerSide = false,
  tableDefinitionId,
  ...rest
}: ColDefWithProperties<{ conditionsList: string[]; isServerSide?: boolean; tableDefinitionId: string }>): ColDef => ({
  headerName,
  field: columnName,
  hide,
  valueGetter: possiblyZeroValueGetter(columnName),
  comparator: generateComparator(columnName, conditionsList),
  chartDataType: 'category',
  filter: 'agMultiColumnFilter',
  filterParams: getFilterParams(isServerSide, tableDefinitionId),
  width: DEFAULT_COLUMN_WIDTH,
  minWidth: MIN_COLUMN_WIDTH,
  ...rest,
});

/** @exportedfortesting */
export const uuidColumn = ({
  field: columnName,
  headerName,
  conditionsList,
  tableDefinitionId,
  ...rest
}: ColDefWithProperties<{ conditionsList: string[]; tableDefinitionId: string }>): ColDef => ({
  headerName,
  field: columnName,
  hide: true,
  comparator: generateComparator(columnName, conditionsList),
  width: DEFAULT_COLUMN_WIDTH,
  minWidth: MIN_COLUMN_WIDTH,
  filterParams: {
    values: async (params) =>
      fetchAndSetValuesForSetColumnFilter(params, () =>
        fetchDistinctValuesForColumn<string>(tableDefinitionId, params.column.getColId(), SET_OPTIONS_FETCH_LIMIT),
      ),
  } as ISetFilterParams,
  ...rest,
});

/** @exportedfortesting */
export const uncertaintyColumn = ({ field: columnName, ...rest }: WithRequired<ColDef, 'field'>): ColDef => ({
  field: columnName,
  headerName: getColumnHeaderName(columnName!),
  hide: false,
  filter: 'agSetColumnFilter',
  valueGetter: (params) => !!params.data?.[columnName],
  tooltipValueGetter: (params) => (params.value ? t('CAPSULES_PANEL.UNCERTAIN_INFO') : undefined),
  cellRenderer: (params: ICellRendererParams) =>
    params.value ? <Icon icon="fa-info-circle" type="darkish-gray" /> : null,
  filterParams: {
    values: [true, false],
    valueFormatter: (params) =>
      params.value
        ? t('VANTAGE.EVIDENCE_TABLE.FILTER_VALUES.IS_UNCERTAIN.TRUE')
        : t('VANTAGE.EVIDENCE_TABLE.FILTER_VALUES.IS_UNCERTAIN.FALSE'),
  } as ISetFilterParams<any, boolean>,
  width: DEFAULT_COLUMN_WIDTH,
  minWidth: MIN_COLUMN_WIDTH,
  ...rest,
});

const structuredBaseColumn = (
  {
    field: columnName,
    headerName,
    conditionsList,
    columnMeta,
    ...rest
  }: ColDefWithProperties<{ conditionsList: string[]; columnMeta: BaseColumnConfiguration }>,
  contextByColumn: React.RefObject<Record<string, string[]>>,
): ColDef => {
  const filterParams = ((): Partial<SetFilterParams> => {
    return (columnMeta?.discreteStates?.length ?? -1) > 0
      ? {
          values: columnMeta?.discreteStates?.map((state) => state.value),
          valueFormatter: (params) => {
            const value = booleanOrValue(params.value);
            const option = columnMeta.discreteStates?.find((s) => s.value === value);
            return translateLabelIfPossible(option) ?? option?.value?.toString() ?? '';
          },
        }
      : {
          values: (params: SetFilterValuesFuncParams) =>
            params.success(contextByColumn.current[columnMeta.colDefName ?? '']),
          refreshValuesOnOpen: true,
        };
  })();

  const filter = hasMultiModalFilter(columnName ?? '') ? MultiModalColumnFilter : 'agSetColumnFilter';

  return {
    headerName,
    field: columnName,
    hide: false,
    comparator: generateComparator(columnName, conditionsList),
    cellRenderer: (params: ICellRendererParams) => {
      const defaultValue = columnMeta?.defaultValue || false;
      const discreteStates = columnMeta?.discreteStates ?? [];
      if (discreteStates?.length > 0) {
        const selection = discreteStates.find((s) => s.value === (params.value || defaultValue));

        return selection ? BooleanColumnRenderer(selection) : null;
      }

      if (Array.isArray(params?.value)) {
        return ArrayColumnRenderer(params?.value);
      }

      return params.value ? SanitizedHtmlColumnRenderer(params.value) : defaultValue;
    },
    filter,
    filterParams,
    valueGetter: (params) => !!params.data?.[columnName as string],
    width: DEFAULT_COLUMN_WIDTH,
    minWidth: MIN_COLUMN_WIDTH,
    ...rest,
  };
};

/** @exportedfortesting */
export const durationColumn = ({ field: columnName, headerName, ...rest }: ColDef): ColDef => ({
  headerName,
  field: columnName,
  valueFormatter: durationValueFormatter,
  aggFunc: 'sum',
  cellClass: 'ag-right-aligned-cell',
  chartDataType: 'series',
  filter: DurationFilter,
  width: DEFAULT_COLUMN_WIDTH,
  minWidth: MIN_COLUMN_WIDTH,
  ...rest,
});

/** @exportedfortesting */
export const timeColumn = ({
  field: columnName,
  headerName,
  aggFunc,
  timeZone,
  ...rest
}: ColDefWithProperties<{ timeZone: string; field: string }>): ColDef => ({
  headerName,
  field: columnName,
  valueFormatter: generateDateValueFormatter(columnName, timeZone),
  aggFunc,
  cellClass: 'ag-right-aligned-cell',
  comparator: generateDateComparator(columnName),
  valueGetter: (params) => params.data?.[`${columnName}_Raw`] ?? params.data?.[columnName],
  chartDataType: 'time',
  width: DEFAULT_COLUMN_WIDTH + 25,
  minWidth: MIN_COLUMN_WIDTH,
  filter: DateColumnFilter,
  filterParams: {
    comparator: generateDateFilterComparator(columnName),
  },
  mainMenuItems: (params) => {
    return params.defaultItems.map((item) => {
      if (item === 'rowGroup') {
        return {
          name: t('VANTAGE.EVIDENCE_TABLE.TIME_SETTINGS.FIELD.TIME_GRANULARITY.GROUP_BY'),
          icon: '<span class="ag-icon ag-icon-group"></span>',
          subMenu: Object.entries(GRANULARITY_OPTIONS).map(([key, option]) => ({
            name: t(option.translationKey),
            action: () => handleGranularityChange(key as keyof typeof GRANULARITY_OPTIONS, columnName!, params.api),
          })),
        };
      }
      return item;
    });
  },
  ...rest,
});

/** @exportedfortesting */
export const referenceColumn = ({
  field: columnName,
  headerName,
  valueGetter,
  valueFormatter,
  conditionsList,
  isServerSide = false,
  tableDefinitionId,
  ...rest
}: ColDefWithProperties<{
  valueFormatter: (params: any) => string;
  conditionsList: string[];
  isServerSide?: boolean;
  tableDefinitionId: string;
}>): ColDef => ({
  field: nameToReferenceKey(columnName ?? ''),
  headerName,
  rowGroup: false,
  hide: columnName !== VantageNames.Columns.Condition,
  valueGetter,
  valueFormatter,
  getQuickFilterText: valueFormatter,
  filter: 'agMultiColumnFilter',
  filterValueGetter: valueFormatter,
  filterParams: getFilterParams(isServerSide, tableDefinitionId),
  comparator: generateComparator(columnName, conditionsList),
  ...rest,
});

const extensionColumnDefinitionFilterTypeToAgGridFilterTypeMap: Record<ExtensionColumnDefinitionFilterType, string> = {
  set: 'agSetColumnFilter',
  text: 'agTextColumnFilter',
  number: 'agNumberColumnFilter',
};

const SERVER_SIDE_STATIC_EXTENSION_COLUMNS: string[] = [
  VantageNames.Columns.Reviewed,
  VantageNames.Columns.Flagged,
  VantageNames.Columns.Labels,
];

const extensionColumn = (
  config: ExtensionColumnConfiguration,
  conditionsList: string[],
  contextByColumn: React.RefObject<Record<string, string[]>>,
  evidenceContextMap: React.RefObject<Map<string, ContextSchema[]>>,
  isServerSide = false,
): ColDef => {
  const columnName = config.colDefName ?? '';
  const defaultValue = config?.colDefDefaultValue ?? null;

  const filterParams = ((): Partial<SetFilterParams> => {
    if (config.colDefFilterType === 'set') {
      const discreteStates = (config?.discreteStates ?? []).filter((state) => !isServerSide || state.value !== 'stale');

      return discreteStates.length > 0
        ? ({
            values: discreteStates.map((state) => state.value),
            valueFormatter: (params) => {
              const value = booleanOrValue(params.value);
              const option = discreteStates.find((s) => s.value === value);

              return translateLabelIfPossible(option) ?? option?.value?.toString() ?? '';
            },
          } as SetFilterParams)
        : {
            values: (params: SetFilterValuesFuncParams) => params.success(contextByColumn.current[columnName]),
            refreshValuesOnOpen: true,
          };
    }

    return { refreshValuesOnOpen: true };
  })();

  const filter = hasMultiModalFilter(columnName)
    ? MultiModalColumnFilter
    : extensionColumnDefinitionFilterTypeToAgGridFilterTypeMap[config.colDefFilterType ?? 'set'] ?? 'agSetColumnFilter';

  const isNumeric = config.colDefType === 'number';
  const decimals =
    isNumeric && _.isNumber(config.colDefDecimals)
      ? Math.max(0, Math.min(config.colDefDecimals, decimalFormatters.length - 1))
      : decimalFormatters.length - 1;

  const defaultAggFunc = isNumeric ? undefined : 'Aggregated';

  return {
    headerName: columnName,
    field: columnName,
    comparator: isServerSide ? undefined : generateComparator(columnName, conditionsList),
    hide: config.colDefHide,
    aggFunc: config.aggFunc || defaultAggFunc,
    wrapText: config.wrapText ?? false,
    autoHeight: config.autoHeight ?? false,
    filter,
    filterParams,
    ...(isNumeric
      ? numericColumnAttributes(
          { current: { [columnName]: decimals } }, // mimic numericColumns ref object
          columnName,
        )
      : {}),
    cellRenderer: isNumeric
      ? undefined
      : (params: ICellRendererParams) => {
          if ((config.discreteStates?.length ?? -1) > 0) {
            const selection = _.find(config.discreteStates, {
              value: params.value || config.colDefDefaultValue,
            });

            return selection ? BooleanColumnRenderer(selection) : null;
          }

          if (Array.isArray(params?.value)) {
            return ArrayColumnRenderer(params?.value);
          }

          return params.value ? SanitizedHtmlColumnRenderer(params.value) : config.colDefDefaultValue;
        },
    valueGetter: isServerSide
      ? (!SERVER_SIDE_STATIC_EXTENSION_COLUMNS.includes(columnName) &&
          ((params) => params.data?.[config.kindIdentifierPairs?.[VantageNames.Kinds.Capsule] ?? columnName])) ||
        undefined
      : (() => {
          if (config.valueGetter) {
            return configValueGetter(config.valueGetter, evidenceContextMap);
          }

          if (config.colDefType === 'boolean') {
            return booleanValueGetter(
              defaultValue as boolean,
              evidenceContextMap,
              config.kindIdentifierPairs?.[VantageNames.Kinds.Capsule],
            );
          }

          return transformationValueGetter(
            defaultValue,
            evidenceContextMap,
            config.kindIdentifierPairs?.[VantageNames.Kinds.Capsule],
            (contextArrayOfInterest) => {
              return applyReduction(
                contextArrayOfInterest,
                config.kindReducerPairs?.[VantageNames.Kinds.Capsule],
                contextContent(config.contextPath ?? ''),
                config.category,
              );
            },
          );
        })(),
    minWidth: config.colDefMinWidth,
    maxWidth: config.colDefMaxWidth,
    ...disableSortingAndFilteringAndGrouping(
      isServerSide &&
        !SERVER_SIDE_STATIC_EXTENSION_COLUMNS.includes(config.colDefName ?? '') &&
        !config.kindIdentifierPairs?.[VantageNames.Kinds.Capsule]?.startsWith(
          `${SeeqNames.SeeqContextNameSpace.UserDefinedContextNamespace}.`,
        ),
    ),
  };
};

export const generateAutoGroupColumnDef = (conditionsList: string[]): ColDef => {
  return {
    sortable: true,
    filter: 'agMultiColumnFilter',
    filterValueGetter: (params) => {
      const showRowGroup = params.colDef?.showRowGroup;
      const colGettingGrouped = typeof showRowGroup === 'string' ? showRowGroup?.replace(/\*$/, '') : '';
      const value = params.getValue(colGettingGrouped);
      return Array.isArray(value) ? value.join('\u00A0') : value ?? '';
    },
    comparator: (valueA, valueB, nodeA, nodeB, _isDescending) => {
      const toCompare = (x: any) => {
        if (!x) {
          return '';
        } else if (typeof x === 'string') {
          return x.toString().toLocaleLowerCase();
        } else {
          return x;
        }
      };

      const colDef = nodeA?.rowGroupColumn?.getColDef();
      const groupedColumnField = colDef?.field;
      const groupedColumnName = groupedColumnField?.replace(/\*$/, '');

      if (groupedColumnName && conditionsList.includes(groupedColumnName)) {
        let nodeAData = '';
        let nodeBData = '';
        if (nodeA?.data && nodeB?.data) {
          nodeAData = nodeA.data?.[groupedColumnName];
          nodeBData = nodeB.data?.[groupedColumnName];
        } else if (nodeA?.aggData && nodeB?.aggData) {
          nodeAData = nodeA.aggData?.[groupedColumnName];
          nodeBData = nodeB.aggData?.[groupedColumnName];
        }
        nodeAData = nodeAData?.toString().toLowerCase() ?? '';
        nodeBData = nodeBData?.toString().toLowerCase() ?? '';
        if (nodeAData === nodeBData) return 0;
        return nodeAData > nodeBData ? 1 : -1;
      }

      const valueAToCompare = toCompare(valueA);
      const valueBToCompare = toCompare(valueB);

      if (valueAToCompare === valueBToCompare) return 0;
      return valueAToCompare > valueBToCompare ? 1 : -1;
    },
  };
};

export const generateDefaultColDef = (): ColDef => {
  return {
    menuTabs: ['generalMenuTab', 'filterMenuTab', 'columnsMenuTab'],
    sortable: true,
    resizable: true,
    enableRowGroup: true,
    enablePivot: true,
    enableValue: true,
    filter: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
    suppressSizeToFit: false,
    suppressAutoSize: false,
    aggFunc: 'Aggregated',
    floatingFilter: true,
    width: DEFAULT_COLUMN_WIDTH,
    minWidth: MIN_COLUMN_WIDTH,
    headerValueGetter: (params) => params.colDef.headerName ?? '',
    valueGetter: whiteSpaceRemoverValueGetter(),
  };
};

const columnOrder = [
  nameToReferenceKey(VantageNames.Columns.Condition),
  VantageNames.Columns.Asset,
  VantageNames.Columns.Start,
  VantageNames.Columns.End,
  VantageNames.Columns.Duration,
  VantageNames.Columns.Path,
  VantageNames.Columns.Flagged,
  VantageNames.Columns.Reviewed,
  VantageNames.Columns.Labels,
  VantageNames.Columns.Suppressed,
];

const columnSortOrder = (a: ColDef, b: ColDef): number => {
  const aIndex = columnOrder.indexOf(a.field ?? '');
  const bIndex = columnOrder.indexOf(b.field ?? '');

  if (aIndex === -1) {
    return 1;
  }

  if (bIndex === -1) {
    return -1;
  }

  return aIndex - bIndex;
};

const numericColumnAttributes = (
  numericColumns: React.RefObject<Record<string, number>>,
  columnName: string,
): ColDef => ({
  filter: 'agNumberColumnFilter',
  filterParams: { defaultOption: 'inRange' },
  valueFormatter: getNumericValueFormatter(numericColumns, columnName),
  defaultAggFunc: 'avg',
  type: 'numericColumn',
});

export const generateColumnsDef = (
  columns: TableColumns | undefined,
  numericColumns: React.RefObject<Record<string, number>>,
  conditionsList: string[],
  evidenceContextMap: React.RefObject<Map<string, ContextSchema[]>>,
  contextByColumn: React.RefObject<Record<string, string[]>>,
  tableDefinitionId: string,
  timeZone = 'UTC',
  t: (path: string | string[]) => string = (path) => (Array.isArray(path) ? path[0] : path),
) => {
  const columnDefs: ColDef[] = [];
  const baseColumns = columns?.base ?? {};
  const extensionColumns = columns?.extensions ?? {};

  const isServerSide = isVantageServerSideRowModelEnabled();

  const addColumn = (columnDef: ColDef) => columnDefs.push(columnDef);

  Object.keys(baseColumns).forEach((columnName) => {
    const headerName = getColumnHeaderName(columnName);

    if (baseColumns[columnName]?.meta?.type?.toString().toLowerCase() === 'reference') {
      addColumn({
        field: columnName,
        headerName: `${columnName} Name`,
        comparator: generateComparator(columnName, conditionsList),
        hide: true,
        filter: isServerSide ? 'agMultiColumnFilter' : undefined,
        filterParams: isServerSide ? getFilterParams(true, tableDefinitionId) : undefined,
      });
      addColumn(
        referenceColumn({
          field: columnName,
          headerName,
          valueGetter: referenceValueGetter(baseColumns[columnName]?.meta?.referenced),
          valueFormatter: generateReferenceColumnValueFormatter(columnName, t),
          conditionsList,
          isServerSide,
          tableDefinitionId,
        }),
      );
    } else if (columnName === VantageNames.Columns.Start) {
      addColumn(timeColumn({ field: columnName, headerName, aggFunc: 'first', timeZone }));
    } else if (columnName === VantageNames.Columns.End) {
      addColumn(timeColumn({ field: columnName, headerName, aggFunc: 'last', timeZone }));
    } else if (columnName === VantageNames.Columns.Duration) {
      addColumn(durationColumn({ field: columnName, headerName }));
    } else if (VANTAGE_UUID_COLUMNS.includes(columnName)) {
      addColumn(
        uuidColumn({
          field: columnName,
          headerName,
          conditionsList,
          tableDefinitionId,
          ...(isServerSide && columnName === VantageNames.Columns.CapsuleID ? { filter: 'agTextColumnFilter' } : {}),
        }),
      );
    } else if (columnName === VantageNames.Columns.IsUncertain) {
      addColumn(uncertaintyColumn({ field: columnName, ...disableSortingAndFilteringAndGrouping(isServerSide) }));
    } else if (columnName === VantageNames.Columns.LastCapsuleState) {
      addColumn({
        field: VantageNames.DerivedColumns.Extinct,
        headerName: getColumnHeaderName(VantageNames.DerivedColumns.Extinct),
        valueGetter: isServerSide
          ? undefined
          : (params) => params.data?.[VantageNames.Columns.LastCapsuleState] === CapsuleEventType.EXTINCT,
        cellRenderer: (params: ICellRendererParams) =>
          params.value === true ? <Icon icon="fa-ban" type="darkish-gray" /> : null,
        tooltipValueGetter: (params) => (params.value ? t('VANTAGE.TOOLTIPS.EXTINCT_CAPSULE') : null),
        filterParams: {
          values: [true, false],
          valueFormatter: (params) =>
            t(
              params.value
                ? 'VANTAGE.EVIDENCE_TABLE.FILTER_VALUES.EXTINCT.TRUE'
                : 'VANTAGE.EVIDENCE_TABLE.FILTER_VALUES.EXTINCT.FALSE',
            ),
        } as SetFilterParams<any, boolean>,
        cellDataType: 'boolean',
        hide: true,
      });
    } else if (baseColumns[columnName].meta.type === 'datetime') {
      addColumn(timeColumn({ field: columnName, headerName, aggFunc: 'last', timeZone }));
    } else if (baseColumns[columnName].meta.type === 'structured') {
      addColumn(
        structuredBaseColumn(
          {
            field: columnName,
            headerName,
            conditionsList,
            columnMeta: baseColumns[columnName].meta,
          },
          contextByColumn,
        ),
      );
    } else {
      addColumn(
        defaultBaseColumn({
          field: columnName,
          headerName,
          hide:
            columnName === VantageNames.Columns.DatumID ||
            columnName === VantageNames.Columns.CapsuleID ||
            ['condition_id', 'uuid'].includes(baseColumns[columnName]?.meta?.type?.toLowerCase() ?? ''),
          conditionsList,
          sortable: !isServerSide || columnName !== VantageNames.Columns.CapsuleID,
          ...(baseColumns[columnName]?.meta?.type?.toLowerCase() === 'numeric'
            ? numericColumnAttributes(numericColumns, columnName)
            : {}),
          isServerSide,
          tableDefinitionId,
        }),
      );
    }
  });

  Object.keys(extensionColumns).forEach((columnName) =>
    addColumn(
      extensionColumn(
        extensionColumns[columnName]?.meta,
        conditionsList,
        contextByColumn,
        evidenceContextMap,
        isServerSide,
      ),
    ),
  );

  addColumn({
    ...itemsInGroupColumn,
    headerName: t(itemsInGroupColumn.headerName!),
    ...disableSortingAndFilteringAndGrouping(isServerSide),
  });
  addColumn({
    ...capsuleCountColumn,
    headerName: t(capsuleCountColumn.headerName!),
    ...disableSortingAndFilteringAndGrouping(isServerSide),
  });

  return columnDefs.sort(columnSortOrder);
};

const getTimeAggregationColumn = (columnName: string, granularity: keyof typeof GRANULARITY_OPTIONS): ColDef => {
  const option = GRANULARITY_OPTIONS[granularity];

  return {
    headerName: `${columnName} ${granularity}`,
    field: columnName,
    valueGetter: (params) => {
      if (!params.data?.[columnName]) return '';
      const formatter = option.formatter();
      return formatter(new Date(params.data[columnName]))!;
    },
    rowGroup: false,
    hide: true,
    colId: `${columnName}_${granularity}`,
  };
};

export const handleGranularityChange = (
  granularity: keyof typeof GRANULARITY_OPTIONS,
  columnName: string,
  api: GridApi,
) => {
  const columnDefs = api.getColumnDefs()!;

  const timeColumn = getTimeAggregationColumn(columnName, granularity);

  if (timeColumn) {
    columnDefs!.push(timeColumn);
    api.setGridOption('columnDefs', columnDefs);

    api.applyColumnState({
      state: [
        {
          colId: timeColumn.colId!,
          rowGroup: true,
          hide: true,
          aggFunc: 'count',
        },
      ],
    });
  }
};
